import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ModalType } from "@/enums";
import { useIsMobile } from "@/hooks/use-responsive";
import { adaptMarginLeft } from "@/utils/adapter";

const HP_PRIMARY = "#024AD8";
const HP_DELETE = "#C92B28";
const HP_TITLE_FONT = "'Forma DJR UI', sans-serif";

const useStyle = ({
  modalType,
  isHp,
}: {
  modalType: ModalType;
  isHp?: boolean;
}) => {
  const { t } = useTranslation();
  const { theme, errorColor, primaryColor } = useGomakeTheme();
  const dir: "rtl" | "ltr" = t("direction");
  const RTL = dir === "rtl";
  const isMobile = useIsMobile();

  const classes = useMemo(() => {
    const base = {
      container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: isMobile ? "90vw" : "-webkit-fill-available",
        minWidth: isMobile ? "unset" : 600,
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        position: "absolute" as "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
        padding: 24,
        zIndex: 9999999999,
      },
      content: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        position: "relative" as "relative",
      },
      icon: {
        display: "flex",
        flexDirection: "column" as "column",
        marginBottom: 16,
      },
      title: {
        ...FONT_FAMILY.Lexend(600, 18),
        color: "#101828",
        marginBottom: 4,
        textAlign: "center" as "center",
        width: "85%",
      },
      closeIcon: {
        display: "flex",
        cursor: "pointer" as "pointer",
        alignSelf: "flex-end",
        width: "fit-content",
        position: "absolute" as "absolute",
        top: 0,
        right: 0,
      },
      subTitle: {
        ...FONT_FAMILY.Lexend(400, 14),
        color: "#475467",
        textAlign: "center" as "center",
        marginBottom: 33,
        width: "89%",
      },
      btnsContainer: {
        display: "flex",
        flexDirection: "row-reverse" as "row-reverse",
        justifyContent: "center",
        alignItems: "center",
        gap: 11,
        width: "100%",
        ...(isMobile && { flexWrap: "wrap" as "wrap" }),
      },
      confirmBtn: {
        height: 44,
        lineHeight: "10px",
        borderRadius: "15px",
        ...FONT_FAMILY.Lexend(600, 16),
        backgroundColor:
          modalType === ModalType.DELETE ? errorColor(500) : primaryColor(500),
        border: `1px solid ${
          modalType === ModalType.DELETE ? errorColor(500) : primaryColor(500)
        }`,
        color: "#FFF",
      },
      cancelBtn: {
        height: 44,
        borderRadius: "15px",
        ...FONT_FAMILY.Lexend(600, 16),
        backgroundColor: "#FFF",
        color: "#344054",
        border: `1px solid #D0D5DD`,
      },
      iconStyle: {
        width: 60,
        height: 60,
        color: errorColor(300),
      },
      children: {
        marginBottom: 20,
      },
      yesBtn: {
        height: 44,
        lineHeight: "18px",
        borderRadius: "15px",
        ...FONT_FAMILY.Lexend(600, 16),
        backgroundColor: primaryColor(500),
        border: `1px solid ${primaryColor(500)}`,
        color: "#FFF",
      },
      noBtn: {
        textAlign: "center",
        height: 44,
        borderRadius: "15px",
        lineHeight: "18px",
        ...FONT_FAMILY.Lexend(600, 16),
        backgroundColor: "#FFF",
        color: primaryColor(500),
        border: `1px solid ${primaryColor(500)}`,
      },
      bottomActionContainer: {
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "8px",
        paddingTop: "8px",
        ...adaptMarginLeft(dir, 16),
      },
      bottomActionButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        textDecoration: "underline",
        color: primaryColor(500),
        ...FONT_FAMILY.Lexend(500, 14),
        padding: 0,
      },
    };

    if (isHp) {
      return {
        ...base,
        container: {
          display: "flex",
          flexDirection: "column" as "column",
          alignItems: "flex-start",
          padding: "0px 0px 40px",
          width: isMobile ? "90vw" : 680,
          maxHeight: 720,
          minWidth: isMobile ? "unset" : 680,
          borderRadius: 16,
          backgroundColor: "#FFFFFF",
          position: "absolute" as "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0px 5px 20px 5px rgba(26, 26, 26, 0.2)",
          zIndex: 9999999999,
        },
        content: {
          display: "flex",
          flexDirection: "column" as "column",
          alignItems: "flex-start",
          width: "100%",
          position: "relative" as "relative",
        },
        icon: {
          display: "none",
        },
        header: {
          display: "flex",
          flexDirection: "column" as "column",
          alignItems: "flex-start",
          padding: "40px 40px 0px",
          gap: 20,
          width: "100%",
        },
        title: {
          fontFamily: HP_TITLE_FONT,
          fontWeight: 500,
          fontSize: 32,
          lineHeight: "32px",
          color: "#1A1A1A",
          marginBottom: 0,
          textAlign: "left" as "left",
          width: "100%",
          minHeight: 40,
          display: "flex",
          alignItems: "center",
        },
        closeIcon: {
          display: "flex",
          cursor: "pointer" as "pointer",
          position: "absolute" as "absolute",
          right: 12,
          top: 12,
          width: 32,
          minWidth: 32,
          height: 32,
          minHeight: 32,
          padding: 6,
          borderRadius: 8,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 3,
        },
        subTitle: {
          fontFamily: HP_TITLE_FONT,
          fontWeight: 400,
          fontSize: 16,
          lineHeight: "24px",
          color: "#1A1A1A",
          textAlign: "left" as "left",
          marginBottom: 0,
          width: "100%",
        },
        btnsContainer: {
          display: "flex",
          flexDirection: "row" as "row",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          padding: "32px 40px 0px",
          gap: 16,
          width: "100%",
        },
        confirmBtn: {
          height: 48,
          backgroundColor:
            modalType === ModalType.DELETE ? HP_DELETE : HP_PRIMARY,
          width: "fit-content",
          minWidth: "112px",
        },
        cancelBtn: {
          height: 48,
        },
        children: {
          marginBottom: 0,
          padding: "20px 40px 0px",
          width: "100%",
          display: "flex",
          flexDirection: "column" as "column",
          alignItems: "flex-start",
        },
      };
    }

    return base;
  }, [theme, RTL, modalType, isMobile, isHp]);

  return {
    classes,
  };
};
export { useStyle };
